import {
  Button,
  Chip,
  Flag,
  type Column,
  IconButton,
  RadioItem,
  Segment,
  Table,
  TableRow,
  TextInput,
} from 'platform/components';
import {Box, HStack, Icon, Link, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {bestOfferId, compareOfferPrice, isCurrencyMismatch} from '../../model/auctionView';
import type {AuctionOffer, AuctionView} from '../../model/auctionView.types';
import {LowRatingFlag} from '../components/LowRatingFlag';
import {RatingStars} from '../components/RatingStars';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {OfferDetailDialog} from './OfferDetailDialog';

type OfferSort = 'price' | 'received' | 'rating';
type OfferFilter = 'all' | 'network';

const isNetworkOffer = (offer: AuctionOffer) => offer.traderNetwork === 'fastback';

const receivedAt = (offer: AuctionOffer) => offer.correctedAt ?? offer.placedAt;

type OfferCompare = (first: AuctionOffer, second: AuctionOffer) => number;

const offerSort = (listingCurrency: string | null): Record<OfferSort, OfferCompare> => ({
  price: compareOfferPrice(listingCurrency),
  received: (first, second) => receivedAt(second).localeCompare(receivedAt(first)),
  rating: (first, second) => (second.traderRating ?? -1) - (first.traderRating ?? -1),
});

function header(label: string, align: 'flex-start' | 'flex-end' = 'flex-start') {
  return (
    <Box paddingVertical={2} paddingHorizontal={2} backgroundColor="palettes.neutral.10.100">
      <HStack justify={align}>
        <Text size="xSmall" color="secondary">
          {label}
        </Text>
      </HStack>
    </Box>
  );
}

interface OffersStepProps {
  vehicleId: string;
  view: AuctionView;
  selectedOfferId: string | null;
  // undefined: the seller cannot change the selection, for example after the winner is confirmed.
  onSelect?: (offerId: string) => void;
  onContinue: () => void;
}

export function OffersStep(props: OffersStepProps) {
  const format = useAuctionFormat();
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const [search, setSearch] = useState<string | null>(null);
  const [sort, setSort] = useState<OfferSort>('price');
  const [filter, setFilter] = useState<OfferFilter>('all');
  const [detailOfferId, setDetailOfferId] = useState<string | null>(null);
  // shortcut: the auction view has no listing currency, so the vehicle selling price gives it. Read it from the view when the BE adds it.
  const listingCurrency =
    vehicle?.sellingPrice?.withVat?.currency ?? vehicle?.sellingPrice?.withoutVat?.currency ?? null;
  const query = (search ?? '').trim().toLowerCase();
  const offers = props.view.offers
    .filter((offer) => filter === 'all' || isNetworkOffer(offer))
    .filter((offer) =>
      [offer.traderName, offer.traderCompany, offer.traderContactName].some((text) =>
        text?.toLowerCase().includes(query)
      )
    )
    // A rejected offer cannot win, so it goes last in every sort.
    .sort(
      (first, second) =>
        Number(first.rejectedAt !== null) - Number(second.rejectedAt !== null) ||
        offerSort(listingCurrency)[sort](first, second)
    );
  const selectedOffer = props.view.offers.find(({offerId}) => offerId === props.selectedOfferId);
  const bestId = bestOfferId(props.view.offers, listingCurrency);

  const columns: Column[] = [
    {element: header(i18n.t('entity.auction.offers.use')), width: 14},
    {element: header(i18n.t('entity.auction.offers.buyerContact'))},
    {element: header(i18n.t('entity.auction.offers.rating')), width: '8.75rem'},
    {element: header(i18n.t('entity.auction.offers.price'), 'flex-end'), width: '11rem'},
    {element: header(i18n.t('general.labels.received')), width: '9.375rem'},
    {element: header(i18n.t('entity.auction.offers.buyerNote'))},
    {element: header(''), width: '6rem'},
  ];

  return (
    <VStack>
      <Box
        paddingHorizontal={4}
        paddingTop={5}
        paddingBottom={3}
        borderBottom="1px solid"
        borderColor="general.separator"
      >
        <HStack spacing={3} align="center" justify="space-between" wrap>
          <HStack spacing={3} align="center" wrap>
            <Box width={['100%', 60]}>
              <TextInput
                value={search}
                onChange={setSearch}
                leftIcon="action/search"
                placeholder={i18n.t('entity.auction.offers.search')}
                data-testid="auction-offers-search"
              />
            </Box>
            <Chip
              label={i18n.t('entity.auction.offers.allOffers', {count: props.view.offers.length})}
              value={filter === 'all'}
              onChange={() => setFilter('all')}
              data-testid="auction-offers-filter-all"
            />
            <Chip
              label={i18n.t('entity.auction.offers.network', {
                count: props.view.offers.filter(isNetworkOffer).length,
              })}
              value={filter === 'network'}
              onChange={() => setFilter('network')}
              data-testid="auction-offers-filter-network"
            />
          </HStack>
          <HStack spacing={2} align="center">
            <Text size="small" color="secondary">
              {i18n.t('entity.auction.offers.sort')}
            </Text>
            <Segment<OfferSort>
              value={sort}
              onChange={setSort}
              options={[
                {value: 'price', label: i18n.t('entity.auction.offers.sortPrice')},
                {value: 'received', label: i18n.t('entity.auction.offers.sortReceived')},
                {value: 'rating', label: i18n.t('entity.auction.offers.sortRating')},
              ]}
              data-testid="auction-offers-sort"
            />
          </HStack>
        </HStack>
      </Box>
      <Show when={props.view.offers.length === 0}>
        <Box padding={4}>
          <Text color="secondary" data-testid="auction-no-offers">
            {i18n.t(
              match(props.view.state)
                .with('bidding', () => 'entity.auction.steps.noOffersYet')
                .with('expired', () => 'entity.auction.offers.emptyExpired')
                .otherwise(() => 'entity.auction.offers.emptyClosed')
            )}
          </Text>
        </Box>
      </Show>
      <Show when={props.view.offers.length > 0}>
        {/* shortcut: no pagination, the table renders all offers. Add paging when a round gets more than 200 offers. */}
        {/* Prototype v11 `.offer-table`: keeps 900px and scrolls horizontally. */}
        <Box width="100%" minWidth={0} overflowX="auto">
          <Box minWidth={225}>
            <Table columns={columns} tableLayout="fixed" data-testid="auction-offers">
              {offers.map((offer) => {
                const select = props.onSelect ? () => props.onSelect?.(offer.offerId) : undefined;
                return (
                  <TableRow
                    key={offer.offerId}
                    onRowClick={select}
                    color={
                      props.selectedOfferId === offer.offerId ? 'palettes.blue.10.100' : undefined
                    }
                    data-testid={`auction-offer-${offer.offerId}`}
                  >
                    <Box paddingVertical={3} paddingLeft={3}>
                      <RadioItem
                        value={props.selectedOfferId === offer.offerId}
                        onChange={select}
                        isDisabled={select === undefined}
                        externalLabelId={`auction-offer-${offer.offerId}-radio`}
                        data-testid={`auction-offer-${offer.offerId}-select`}
                      />
                      {/* RadioItem has no aria-label, so a hidden label names the radio for screen readers. */}
                      <Box position="absolute" width={0} height={0} overflow="hidden">
                        <label htmlFor={`auction-offer-${offer.offerId}-radio`}>
                          {i18n.t('entity.auction.offers.useOfferOf', {
                            name: offer.traderName,
                            defaultValue: 'Use offer of {name}',
                          })}
                        </label>
                      </Box>
                    </Box>
                    <Box paddingVertical={3} paddingHorizontal={2}>
                      <HStack spacing={2} align="center" wrap>
                        <Text alternative color="link" overflowWrap="anywhere">
                          {offer.traderName}
                        </Text>
                        <Flag label={offer.traderTier} colorScheme="blue" size="small" isSubtle />
                      </HStack>
                      <Text size="xSmall" color="secondary" overflowWrap="anywhere">
                        {[offer.traderCompany, offer.traderCity, offer.traderContactName]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                      <Show when={offer.traderPhone}>
                        {(phone) => (
                          <HStack spacing={1} align="center">
                            <Icon value="communication/phone" size={3} color="text.secondary" />
                            <Text size="xSmall" color="secondary">
                              {phone}
                            </Text>
                          </HStack>
                        )}
                      </Show>
                      <Show when={offer.traderEmail}>
                        {(email) => (
                          <HStack spacing={1} align="center">
                            <Icon value="communication/email" size={3} color="text.secondary" />
                            <Link size="xSmall" href={`mailto:${email}`} overflowWrap="anywhere">
                              {email}
                            </Link>
                          </HStack>
                        )}
                      </Show>
                      <Text size="xSmall" color="tertiary">
                        {format.network(offer.traderNetwork)}
                      </Text>
                    </Box>
                    <Box paddingVertical={3} paddingHorizontal={2}>
                      <HStack spacing={1} align="center">
                        <RatingStars rating={offer.traderRating} />
                        <Text size="small" alternative noWrap>
                          {offer.traderRating === null
                            ? i18n.t('entity.auction.offers.notRated')
                            : i18n.t('entity.auction.offers.ratingValue', {
                                rating: format.rating(offer.traderRating),
                              })}
                        </Text>
                      </HStack>
                      <LowRatingFlag
                        offer={offer}
                        testId={`auction-offer-${offer.offerId}-low-rating`}
                      />
                      <Show when={offer.traderRatingsCount !== null}>
                        <Text size="small" color="secondary">
                          {format.count(
                            'entity.auction.offers.ratingsCount',
                            offer.traderRatingsCount ?? 0
                          )}
                        </Text>
                      </Show>
                      <Show when={offer.traderTransactionsCount !== null}>
                        <Text size="small" color="secondary">
                          {format.count(
                            'entity.auction.offers.transactionsCount',
                            offer.traderTransactionsCount ?? 0
                          )}
                        </Text>
                      </Show>
                    </Box>
                    <Box paddingVertical={3} paddingHorizontal={2}>
                      <VStack align="flex-end">
                        <Text size="large" alternative strikethrough={offer.rejectedAt !== null}>
                          {format.money(offer.amount)}
                        </Text>
                        <Show when={offer.rejectedAt !== null}>
                          <Flag
                            label={i18n.t('entity.auction.offers.offerRejected')}
                            colorScheme="red"
                            size="small"
                            isSubtle
                          />
                        </Show>
                        <Show when={isCurrencyMismatch(offer, listingCurrency)}>
                          <Flag
                            label={i18n.t('entity.auction.offers.currencyMismatch', {
                              defaultValue: 'Different currency',
                            })}
                            colorScheme="orange"
                            size="small"
                            isSubtle
                          />
                        </Show>
                        <Show when={offer.offerId === bestId}>
                          <Flag
                            label={i18n.t('entity.auction.offers.best')}
                            colorScheme="green"
                            size="small"
                            isSubtle
                            data-testid={`auction-offer-${offer.offerId}-best`}
                          />
                        </Show>
                        <Show when={vehicle?.isVatDeductible === false}>
                          <Text size="small" color="secondary">
                            {i18n.t('entity.auction.confirmation.vatNotDeductible')}
                          </Text>
                        </Show>
                      </VStack>
                    </Box>
                    <Box paddingVertical={3} paddingHorizontal={2}>
                      <Text>{format.dateTime(receivedAt(offer))}</Text>
                    </Box>
                    <Box paddingVertical={3} paddingHorizontal={2}>
                      {/* eslint-disable-next-line react/forbid-dom-props -- No platform prop clamps lines. The full note is in the offer detail. */}
                      <div className="line-clamp-3">
                        <Text size="small" overflowWrap="anywhere">
                          {offer.note ?? EMPTY_PLACEHOLDER}
                        </Text>
                      </div>
                    </Box>
                    <Box paddingVertical={3} paddingRight={3}>
                      <HStack spacing={2} justify="flex-end">
                        <IconButton
                          icon="action/search"
                          priority="secondary"
                          size="small"
                          aria-label={i18n.t('entity.auction.offers.offerDetail', {
                            name: offer.traderName,
                          })}
                          onClick={(event) => {
                            event.stopPropagation();
                            setDetailOfferId(offer.offerId);
                          }}
                          data-testid={`auction-offer-${offer.offerId}-detail`}
                        />
                        {/* shortcut: buyer chat is not built yet, so the button stays disabled. */}
                        <IconButton
                          icon="communication/chat"
                          priority="secondary"
                          size="small"
                          isDisabled
                          aria-label={i18n.t('entity.auction.offers.messagesFor', {
                            name: offer.traderName,
                          })}
                          data-testid={`auction-offer-${offer.offerId}-chat`}
                        />
                      </HStack>
                    </Box>
                  </TableRow>
                );
              })}
            </Table>
          </Box>
        </Box>
      </Show>
      <OfferDetailDialog
        offer={props.view.offers.find(({offerId}) => offerId === detailOfferId) ?? null}
        roundNumber={props.view.roundNumber}
        isVatNotDeductible={vehicle?.isVatDeductible === false}
        onClose={() => setDetailOfferId(null)}
        onSelect={props.onSelect}
      />
      {/* Prototype v11 `.selected-offer-bar`: pinned to the viewport bottom while the offers scroll. */}
      <Box
        position="sticky"
        bottom={0}
        zIndex="CONTENT"
        paddingHorizontal={4}
        paddingVertical={3}
        borderTop="1px solid"
        borderColor="palettes.neutral.600.100"
        borderBottomLeftRadius="small"
        borderBottomRightRadius="small"
        backgroundColor="palettes.neutral.900.100"
        boxShadow="elevation_2"
        data-testid="auction-selection-bar"
      >
        <HStack spacing={4} align="center" wrap>
          <Box minWidth={0}>
            <Text size="xSmall" color="tertiary">
              {i18n.t(
                selectedOffer
                  ? 'entity.auction.offers.selectedOffer'
                  : 'entity.auction.offers.noOfferSelected'
              )}
            </Text>
            <Text size="small" color="white" alternative>
              {selectedOffer?.traderName ??
                (props.onSelect ? i18n.t('entity.auction.offers.pickRow') : EMPTY_PLACEHOLDER)}
            </Text>
          </Box>
          <Box>
            <Text size="xSmall" color="tertiary">
              {i18n.t('entity.auction.offers.price')}
            </Text>
            <Text color="white" alternative strikethrough={Boolean(selectedOffer?.rejectedAt)}>
              {selectedOffer ? format.money(selectedOffer.amount) : EMPTY_PLACEHOLDER}
            </Text>
          </Box>
          <Show when={selectedOffer?.rejectedAt}>
            <Flag
              label={i18n.t('entity.auction.offers.offerRejected')}
              colorScheme="red"
              size="small"
              data-testid="auction-selection-rejected"
            />
          </Show>
          <Box flex={1} />
          <Button
            variant="primary"
            title={i18n.t('entity.auction.offers.continue')}
            isDisabled={selectedOffer === undefined}
            onClick={props.onContinue}
            data-testid="auction-continue-to-confirmation"
          />
        </HStack>
      </Box>
    </VStack>
  );
}
